import React from 'react';
import { cn } from '@rocco/utils/cn';

/**
 * Props for the StepIndicator component
 */
interface StepIndicatorProps {
    /** Current step index (0-based) */
    currentStepIndex: number;
    /** Total number of steps */
    totalSteps: number;
    /** Current visual dot index (0-based, accounts for steps that share dots) */
    currentVisualDotIndex?: number;
    /** Total number of visual dots to display (accounts for steps that share dots) */
    totalVisualDots?: number;
    /** Additional CSS classes */
    className?: string;
    /** Whether to show text "Step X of Y" or segmented bar instead of dots */
    variant?: 'dots' | 'text' | 'bar';
    /** Custom label shown instead of "Step X/Y" in the bar variant */
    label?: string;
}

const STEP_INDICATOR_BAR_SEGMENTS = 8;

/**
 * Step indicator component that displays current progress through the form
 * Shows "Step X of Y" format with responsive styling
 */
export function StepIndicator({
    currentStepIndex,
    totalSteps,
    currentVisualDotIndex,
    totalVisualDots,
    className,
    variant = 'dots',
    label,
}: StepIndicatorProps) {
    // Use visual indices if provided, otherwise fall back to actual step indices
    const visualDotIndex = currentVisualDotIndex ?? currentStepIndex;
    const dotCount = totalVisualDots ?? totalSteps;
    const currentStepNumber = visualDotIndex + 1; // Convert from 0-based to 1-based

    if (variant === 'text') {
        return (
            <div
                className={cn(
                    'flex items-center justify-center text-center leading-[1.2] font-medium',
                    'text-[13px] text-[#A3A4A6]',
                    className,
                )}
                role="status"
                aria-label={`Step ${currentStepNumber} of ${dotCount}`}
            >
                Step {currentStepNumber} of {dotCount}
            </div>
        );
    }

    if (variant === 'bar') {
        const segments = Math.min(dotCount, STEP_INDICATOR_BAR_SEGMENTS);
        return (
            <div
                className={cn(
                    'relative -top-3 left-0 flex w-full flex-col gap-2 md:-top-2 md:flex-row md:items-center',
                    className,
                )}
                role="status"
                aria-label={label ?? `Step ${currentStepNumber} of ${dotCount}`}
            >
                <span className="block text-center font-medium text-[#2A2A2A] uppercase md:hidden">
                    {label ? (
                        <span className="text-[0.6875rem] leading-[1.2]">
                            {label}
                        </span>
                    ) : (
                        <span className="text-[0.6875rem]">
                            Step {currentStepNumber}/{dotCount}
                        </span>
                    )}
                </span>
                <span className="hidden text-center text-3 text-[#2A2A2A] md:block">
                    Step {currentStepNumber}/{dotCount}
                </span>
                <div className="mx-auto flex h-0.75 w-69 flex-1 gap-0.5 overflow-hidden transition-colors md:w-full">
                    {Array.from({ length: segments }).map((_, index) => (
                        <div
                            key={index}
                            className={cn(
                                'h-1.5 flex-1 transition-colors',
                                index <= visualDotIndex
                                    ? 'bg-[#afafaf] md:bg-[#38383B]'
                                    : 'bg-[#afafaf]/30 md:bg-[#D4D4D4]',
                            )}
                        />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div
            className={cn('flex items-center justify-center', className)}
            role="status"
            aria-label={`Step ${currentStepNumber} of ${dotCount}`}
        >
            <div className="flex items-center justify-center gap-x-4">
                {Array.from({ length: dotCount }).map((_, index) => (
                    <div
                        key={index}
                        className={cn(
                            'flex h-3 w-3 items-center justify-center rounded-full bg-gray-300',
                            visualDotIndex === index && 'bg-brick-500',
                        )}
                    />
                ))}
            </div>
        </div>
    );
}
